// #4413 and df-26e08e4c2f75: bound the wait for a page's lifecycle hooks, so one slow
// page cannot wedge the whole nav restore.
//
// The problem this solves. cs-nav.ts waits on two page-supplied promises during a single
// page change: the entering page's ionViewWillEnter, and then the leaving page's
// ionViewDidLeave. Both waits exist only so an exception from the page gets reported —
// nothing visual depends on either, because the page transition has already finished by
// the time they run. Both were wrapped in a try/catch, which handles a promise that
// REJECTS and does nothing at all for a promise that never SETTLES.
//
// A page whose hook never returns therefore stopped the rest of the navigation from ever
// running: no ionViewDidEnter, no screen-name change, and, on a boot that was restoring
// the user's last screen, the "restore in progress" flag was never cleared. Twenty
// seconds later the nav-restore watchdog declared the restore wedged, reported
// NavRestoreStuck and force-navigated the user to Home. In the 14 days to 2026-07-31 the
// ENTRY-side version of that happened 376 times to 115 people, on iOS, Android and the
// web build. What they saw was a dead spinner for twenty seconds.
//
// The leave side has never been seen to fire, and cannot today: every ionViewDidLeave in
// the app does only synchronous work and the base-class one is empty, so none of them can
// hang. It is bounded anyway because the door is held shut by nothing more than that
// coincidence — one added `await` in any page's leave hook opens it — and because the
// consequence is the same twenty-second dead spinner.
//
// What this module changes. Each wait is bounded. If the page does not finish in time we
// stop waiting, say which page it was, and let the navigation carry on — the user gets a
// partly-loaded page they can actually use instead of a frozen app. It does NOT fix
// whatever is hanging inside the page; that is still unknown, and naming the page in the
// report is how we will find it.
//
// It also owns what the entry-side report is CALLED, because a page that ran out of time
// and a page that was destroyed while it was still working are two different problems that
// must not share one name. chooseEnterTimeoutReportName() below is that decision and says
// why it has to be the name rather than a field.
//
// Why this is two functions and not one. Attaching the rejection handler and waiting for
// the result happen at two different moments in cs-nav.ts, and they have to. The entering
// page's promise is created early, before the page-slide animation, and the result is
// only wanted after it — a whole transition later. A browser decides a promise is
// "unhandled" at the end of the current chunk of work, so a handler attached at the
// waiting end is attached far too late: a page that failed early was reported by the
// browser as a raw unhandled rejection instead of through our own trackAbnormalException,
// with none of the page context we meant to send. So capturePageHookOutcome() runs the
// instant the promise is made, and awaitPageHookBoundedAsync() does the waiting later.
// Splitting them rather than moving the whole wait earlier keeps PAGE_ENTER_TIMEOUT_MS
// meaning what its comment below says it means — a budget for the page's own entry work,
// not one that is already ticking down during the slide.
//
// The leave site has no such gap — it creates the promise and wants the answer straight
// away — so it calls both functions on one line. It still goes through capture rather
// than handing the raw promise to the wait, because that is what keeps a page rejecting
// LATE, after we gave up on it, from resurfacing as an unhandled rejection.
//
// It also owns one thing about the THIRD page lifecycle hook, ionViewCanEnter, which
// is not waited for here at all: how cs-nav.ts tells a deliberate refusal to show a
// page from an unexpected crash while deciding. That is PageGuardRefusalError at the
// bottom of this file, and it lives here because it belongs to the same question as
// everything above — what cs-nav does when a page lifecycle hook does not simply
// finish. See its own comment for why it was needed.
//
// Pure so it runs under node for unit tests (cs-nav.ts is an Angular component and
// cannot be loaded there) — same pattern as nav-restore-coordination.ts beside it.
// cs-nav.ts holds the side effects and delegates the waiting decision here.

/** How long ONE page gets to finish ionViewWillEnter before we stop waiting for it.
 *
 * This is a PER-PAGE budget, and that distinction matters. A boot that restores a
 * single page now reports and steps over a stalled page well inside the nav-restore
 * watchdog's own 20 s (NAV_RESTORE_WATCHDOG_MS in nav-restore-coordination.ts), so
 * the watchdog never fires. A boot restoring a TRAIL does not get that guarantee:
 * restoreNavigationStack pushes trail pages one at a time and each push comes through
 * here, so two stalled pages still add up past the watchdog's budget and it fires as
 * before. No single per-page number can prevent that, because the trail length is not
 * known here. What the bound does buy in the trail case is the report — we learn which
 * page stalled, which is what nothing recorded before. */
export const PAGE_ENTER_TIMEOUT_MS = 10_000;

/** How long the OUTGOING page gets to finish ionViewDidLeave before we stop waiting.
 *
 * Deliberately smaller than PAGE_ENTER_TIMEOUT_MS, and the reason is arithmetic rather
 * than any belief that leaving is quicker than entering. Both waits happen in the same
 * page change, one after the other, so a page that stalls in BOTH hooks spends the two
 * budgets ADDED TOGETHER. At 10 s each that is exactly NAV_RESTORE_WATCHDOG_MS, which
 * would let the watchdog fire on the very case this guard exists to prevent. At 10 s
 * plus 5 s the worst case is 15 s and the watchdog still never fires.
 *
 * The same per-page-not-per-restore limit as the entry budget applies: a restored trail
 * of several stalled pages can still out-wait the watchdog, and no per-page number can
 * change that. See PAGE_ENTER_TIMEOUT_MS above. */
export const PAGE_LEAVE_TIMEOUT_MS = 5_000;

/** What happened while waiting for one of a page's lifecycle hooks. */
export type PageHookOutcome =
  /** the page finished that hook's work normally */
  | { kind: 'completed' }
  /** the page threw; this is the case the original try/catch was written for */
  | { kind: 'failed'; error: unknown }
  /** the page neither finished nor threw within the budget */
  | { kind: 'timedOut'; timeoutMs: number };

/**
 * Start watching a page lifecycle hook's promise, and turn whatever it does into a value.
 *
 * Call this the moment the page's promise is created, NOT when its result is wanted.
 * Doing so is the whole point of the function: it attaches the success and failure
 * handlers straight away, so the browser never sees the page's failure as unhandled and
 * the error stays ours to report. The returned promise never rejects, so it is safe to
 * hold on to for as long as the caller likes before awaiting it.
 *
 * It is deliberately NOT bounded in time — that is the other half's job. This one only
 * makes sure a failure has somewhere to go.
 */
export function capturePageHookOutcome(hookResult: Promise<unknown>): Promise<PageHookOutcome> {
  return hookResult.then(
    () => ({ kind: 'completed' }),
    error => ({ kind: 'failed', error }),
  );
}

/**
 * Wait for a page lifecycle hook's captured outcome, but never for longer than
 * `timeoutMs`. Takes what capturePageHookOutcome() returned, not the page's own promise.
 *
 * This never rejects. It reports which of the three things happened and leaves the
 * caller to decide what to report and how to carry on — the caller is an Angular
 * component with the logging service, this module stays free of both.
 *
 * Deliberately does NOT use `timeout()` from `@cf-shared/shared-util`, even though it
 * looks like the obvious fit. That helper signals expiry by rejecting with the SHARED
 * `TimeoutError` class, which pages and services all over the app also throw. We would
 * then have to tell "we stopped waiting" from "the page itself timed out on something"
 * by class alone, which cannot be done — so a page's own timeout would be reported as
 * ours and its error thrown away. Racing our own promise keeps the two distinguishable
 * by construction. It also means a `timeoutMs` of 0 still bounds the wait, where
 * `timeout()` treats 0 as "wait forever".
 */
export async function awaitPageHookBoundedAsync(
  hookSettled: Promise<PageHookOutcome>,
  timeoutMs: number,
): Promise<PageHookOutcome> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const ranOutOfTime: Promise<PageHookOutcome> = new Promise(resolve => {
    timer = setTimeout(() => resolve({ kind: 'timedOut', timeoutMs }), timeoutMs);
  });
  try {
    return await Promise.race([hookSettled, ranOutOfTime]);
  } finally {
    // if the page won the race, drop the pending timer rather than leaving it to fire
    clearTimeout(timer);
  }
}

/** The report name for a page that is still on screen and has simply not finished.
 *
 * Byte-identical to what this report has always been called, and it must stay that way:
 * the name is the Sentry grouping key, so changing it would abandon the existing issue
 * and its history. */
export const PAGE_ENTER_TIMEOUT_REPORT = 'PageWillEnterTimeout';

/** The report name for a page whose entry work outlived the page itself. */
export const PAGE_ENTER_ORPHANED_REPORT = 'PageWillEnterOrphanedAfterDestroy';

/**
 * What to call the report when the entering page missed PAGE_ENTER_TIMEOUT_MS.
 *
 * df-7d1bc760ec40. Two quite different things reach that branch and they used to arrive
 * under one name:
 *
 *   - The page is still on screen and is genuinely stuck. This is the live field symptom
 *     #4413 was built to attribute, and the one the module header describes — the user is
 *     looking at a half-loaded page right now.
 *   - The page was destroyed by a LATER navigation while its ionViewWillEnter was still
 *     running (see CsNavComponent.setRoot, which destroys the previous page once the new
 *     one is active). Nobody is looking at that page, and the report arrives ten seconds
 *     after the user moved on, so none of the wording above applies to it.
 *
 * Mixing them makes the first signal unreadable, which is the whole point of collecting
 * it. So they get separate names.
 *
 * Both stay at the same "someone should look eventually" tier, and the second one has to
 * be argued for rather than assumed, because what it means changed recently. It used to
 * mean the page's entry work was orphaned for the lifetime of the tab: waitForAngularUpdate
 * waited on a render callback that Angular drops on destroy, so the promise never settled
 * at all (df-3f0eaff27ac6, since fixed — that wait now returns when the component is
 * destroyed). Today the work does finish, so it is no longer a leak. What is left still
 * says two things went wrong together: two navigations raced, which design-docs/navigation.md
 * calls a bug in the caller, AND the loser's entry chain took longer than the whole
 * ten-second budget to wind down even though the destroy released its render wait
 * immediately — so something in that chain is genuinely slow, or is not checking its
 * destroy guard. That is not "nothing is wrong", which is what the informational tier
 * would claim.
 *
 * **It has to be the NAME and cannot be a field.** LoggingService reports these as the
 * Sentry message `PROBLEM: <name>`, so the name is what groups the issue, and it is the
 * whole of what reaches Mixpanel (as the `log_message` property of `sentry_error`). The
 * `context` object beside it becomes a Sentry breadcrumb only. So the obvious cheap
 * version of this fix — adding `destroyed: true` to the context — would be invisible in
 * both tools and would separate nothing.
 *
 * Pure, and takes a plain boolean rather than the ComponentRef, so it can be tested under
 * node: cs-nav.ts reads `componentRef.hostView.destroyed` and passes the answer in.
 */
export function chooseEnterTimeoutReportName(pageWasDestroyed: boolean): string {
  return pageWasDestroyed ? PAGE_ENTER_ORPHANED_REPORT : PAGE_ENTER_TIMEOUT_REPORT;
}

/**
 * How many open entry steps are remembered at once (#4537).
 *
 * A page whose entry work never settles leaves its step open for the rest of the
 * session, so without a ceiling this would grow for as long as the app stays open.
 *
 * Past this many, the NEWEST arrival is refused and the entries already held are kept.
 * That direction is the whole point: `openStep()` reports the OLDEST step, because when
 * one step is slow it is the one holding the time up. Evicting the oldest — the obvious
 * implementation — would throw away precisely the entry this class exists to report.
 * Same reasoning, and the same direction, as LOCAL_OP_TRACKER_CAPACITY in
 * services/local-op-tracker.ts.
 */
export const PAGE_ENTRY_STEP_CAPACITY = 32;

/**
 * How many FINISHED entry steps are remembered, across every page (#4988).
 *
 * Past this many the OLDEST is dropped — the opposite of PAGE_ENTRY_STEP_CAPACITY, on
 * purpose. An old open step is the one holding everything up; an old finished step
 * belongs to a page change long gone and is worth least. A whole entry of Home fits with
 * room to spare, including the parts of its day load that are timed on their own (#4988's
 * second reading, which raised this from 16).
 */
export const PAGE_ENTRY_DONE_STEP_CAPACITY = 32;

/** One step of a page's entry work that has started and has not finished. */
export interface OpenPageEntryStep {
  /** Which page opened it, as its element tag name, e.g. `page-home`. */
  page: string;
  /** What the page is doing, e.g. `loadCurrentDay`. Never a document body or user value. */
  name: string;
  /** How long it has been running, in milliseconds. */
  ageMs: number;
}

/**
 * Which step of a page's `ionViewWillEnter` is still running (#4537).
 *
 * Why this exists. #4413 bounded the wait for a page's entry work and made the report
 * name the PAGE. Reading three weeks of that report answered the question — it is almost
 * always `page-home` — and immediately raised the next one: `HomePage.ionViewWillEnter`
 * awaits a series of things in a row, and nothing said which of them had not come back.
 * This records that, so the report can say it. The figures behind that reading are in
 * design-docs/boot-sequence.md, "What the #4413 report says, three weeks on".
 *
 * It is REPORT-ONLY. It never abandons a step, never rejects, and never changes what the
 * page does. It is deliberately the same shape as `LocalOpTracker` (#4401) — handles
 * rather than names as keys, oldest-wins, injected clock — because the two answer the
 * same kind of question about different layers, and a reader who knows one knows both.
 * They are separate classes rather than one shared one because their vocabularies do not
 * overlap: that one names LOCAL DATABASE operations, this one names steps of a page's
 * lifecycle hook, and merging them would make every report ambiguous about which it meant.
 *
 * Kept here, in the module that already owns the bounded wait and the report's name, so
 * it stays free of Angular and runs under node for unit tests.
 */
export class PageEntryStepTracker {
  // handle -> { page, name, startedAt }. Nothing relies on Map order: entries are removed
  // by handle, and openStep() compares startedAt rather than trusting position.
  private open = new Map<number, { page: string; name: string; startedAt: number; entry: number }>();
  private nextHandle = 1;
  // how many begin() calls were refused because the map was full. Reported alongside the
  // open step so a saturated tracker is never mistaken for a quiet one — see
  // PAGE_ENTRY_STEP_CAPACITY.
  private refusedCount = 0;
  // #4988: steps that have finished, oldest first, capped at PAGE_ENTRY_DONE_STEP_CAPACITY.
  // The open step alone is only whatever happens to be running when the bound runs out;
  // these say how long the steps before it took, which is where the time usually went.
  private done: { page: string; name: string; durationMs: number }[] = [];
  // #4988: page -> how many entries beginEntry has marked. Each open step remembers the
  // number it started under, so a step of an earlier visit that ends during a later one
  // is not listed as the later visit's.
  private entryOf = new Map<string, number>();

  constructor(private readonly now: () => number = () => performance.now()) {}

  /**
   * Record that a step has started. Returns a handle to pass to `end`.
   *
   * At capacity the new arrival is REFUSED, keeping the older entries (see
   * PAGE_ENTRY_STEP_CAPACITY for why that direction matters). The returned handle is
   * still valid to pass to `end` — it simply matches nothing — because the caller's
   * `finally` cannot know whether its step was recorded.
   */
  begin(page: string, name: string, entry: number = this.currentEntry(page)): number {
    const handle = this.nextHandle++;
    if (this.open.size >= PAGE_ENTRY_STEP_CAPACITY) {
      this.refusedCount++;
      return handle;
    }
    this.open.set(handle, { page, name, startedAt: this.now(), entry });
    return handle;
  }

  /**
   * Which entry of `page` is current (#4988). A page reads this once at the top of its
   * `ionViewWillEnter` and passes it to every step, because a visit can outlast the next
   * `beginEntry`: its later steps would otherwise pick up the NEW entry's number and be
   * listed as that visit's.
   */
  currentEntry(page: string): number {
    return this.entryOf.get(page) ?? 0;
  }

  /**
   * Record that `page` is starting a new entry, forgetting the finished steps of its
   * previous one (#4988). cs-nav calls this right before `ionViewWillEnter`. Without it a
   * report on a second visit would list both visits' steps as if they were one entry, and
   * coming back to Home seconds later is ordinary use, so no time window could tell them
   * apart. Open steps are left alone: one still running from the earlier visit is exactly
   * what the report should name. When it does end, `end()` sees it started under an
   * earlier entry and does not list it as this visit's.
   */
  beginEntry(page: string): void {
    this.entryOf.set(page, this.currentEntry(page) + 1);
    this.done = this.done.filter(done => done.page !== page);
  }

  /** Record that the step behind this handle has finished, and how long it took. */
  end(handle: number): void {
    const step = this.open.get(handle);
    // if the handle was refused at capacity there is no start time, so no duration to record
    if (!step) return;
    this.open.delete(handle);
    // if the page has been entered again since this step started, it belongs to no visit
    // the report will ask about
    if (step.entry !== this.currentEntry(step.page)) return;
    this.done.push({ page: step.page, name: step.name, durationMs: this.now() - step.startedAt });
    // if over the ceiling, drop the oldest: an old finished step is the one worth least
    if (this.done.length > PAGE_ENTRY_DONE_STEP_CAPACITY) this.done.shift();
  }

  /**
   * The step that has been running longest, or undefined if none is.
   *
   * Longest, not most recent: the steps run one after another, so a slow one is what the
   * later ones are waiting for. Reporting the newest would name a step that had barely
   * begun.
   */
  openStep(): OpenPageEntryStep | undefined {
    let oldest: { page: string; name: string; startedAt: number } | undefined = undefined;
    for (const step of this.open.values()) {
      if (!oldest || step.startedAt < oldest.startedAt) {
        oldest = step;
      }
    }
    if (!oldest) return undefined;
    return { page: oldest.page, name: oldest.name, ageMs: this.now() - oldest.startedAt };
  }

  /**
   * The step that started most recently on the same page as the oldest open step, other
   * than that oldest step itself — or undefined (#4988's second reading).
   *
   * A step can run smaller steps inside it: Home's day load times each of its reads on
   * its own. Then two steps are open at once, and `openStep()` names the outer one, which
   * says nothing new. This names the innermost one, the read that is actually waiting.
   * When a step from an earlier visit of the page is still open, the oldest step is that
   * one, and this names the step the current visit is on. Either way it is the most
   * recently started step, the one actually waiting.
   */
  innerStep(): OpenPageEntryStep | undefined {
    const outerHandle = this.oldestOpenHandle();
    if (outerHandle === undefined) return undefined;
    const outer = this.open.get(outerHandle)!;
    let newest: { page: string; name: string; startedAt: number } | undefined = undefined;
    for (const [handle, step] of this.open) {
      // if this is the outer step itself, or another page's step, it is not running inside it
      if (handle === outerHandle || step.page !== outer.page) continue;
      if (!newest || step.startedAt >= newest.startedAt) {
        newest = step;
      }
    }
    if (!newest) return undefined;
    return { page: newest.page, name: newest.name, ageMs: this.now() - newest.startedAt };
  }

  /** The handle of the step that has been running longest, the one `openStep()` names. */
  private oldestOpenHandle(): number | undefined {
    let oldest: { handle: number; startedAt: number } | undefined = undefined;
    for (const [handle, step] of this.open) {
      if (!oldest || step.startedAt < oldest.startedAt) {
        oldest = { handle, startedAt: step.startedAt };
      }
    }
    return oldest?.handle;
  }

  /** How many begin() calls were refused at capacity. */
  refusedAtCapacityCount(): number {
    return this.refusedCount;
  }

  /**
   * Flat telemetry properties for a timeout report. EMPTY when no step is open and
   * nothing was ever refused.
   *
   * Empty rather than zeroed on purpose: an absent property reads as "no step was open",
   * which is a different fact from "a step was open for 0 ms". The first says the page
   * is not instrumented, or had already finished its marked work; the second would claim
   * a measurement nobody made.
   *
   * `page_entry_step_page` says which page opened the step, and it is NOT always the page
   * the report names. This tracker is one shared object, `openStep()` answers with the
   * oldest step whoever opened it, and a page whose entry work is still running keeps its
   * step open while a LATER page enters and times out. Without this field that second
   * report would carry the first page's step and read exactly like a measurement of
   * itself. The caller compares the two — see the report site in cs-nav.ts.
   *
   * The refusal count is reported on its own even when nothing is open any more, for the
   * same reason LocalOpTracker reports its own: once the ceiling is reached every later
   * step is silently dropped, so a saturated tracker keeps naming one stale step with an
   * ever-growing age and nothing else would say it had stopped recording.
   */
  snapshot(): Record<string, string | number> {
    // only when it happened — an absent property reads as "the tracker saw everything",
    // which is a different fact from "it refused none this time"
    const refused = this.refusedCount > 0 ? { page_entry_steps_refused_count: this.refusedCount } : {};
    const step = this.openStep();
    if (!step) return refused;
    const doneSteps = this.finishedStepsOf(step.page);
    const inner = this.innerStep();
    return {
      page_entry_step: step.name,
      page_entry_step_ms: Math.round(step.ageMs),
      page_entry_step_page: step.page,
      page_entry_steps_open_count: this.open.size,
      // only when a smaller step is running inside the open one — absent reads as "the
      // open step was not waiting on any part that is timed on its own"
      ...(inner ? { page_entry_inner_step: inner.name, page_entry_inner_step_ms: Math.round(inner.ageMs) } : {}),
      // only when something finished — absent reads as "nothing of this page had finished"
      ...(doneSteps ? { page_entry_done_steps: doneSteps } : {}),
      ...refused,
    };
  }

  /**
   * How long each finished step of `page`'s current entry took, as `name:ms` pairs joined
   * by commas, oldest first — or undefined when none has finished (#4988).
   *
   * Only the page that owns the open step, for the reason page_entry_step_page exists:
   * the tracker is shared, and another page's steps must never read as this page's.
   * `beginEntry` has already dropped the steps of that page's earlier visits. One flat
   * string, because the report's properties must be flat; step names come from a fixed
   * list and carry no user data.
   */
  private finishedStepsOf(page: string): string | undefined {
    const ofPage = this.done.filter(done => done.page === page);
    if (ofPage.length === 0) return undefined;
    return ofPage.map(done => `${done.name}:${Math.round(done.durationMs)}`).join(',');
  }
}

/** The one shared tracker for the app (same convention as `localOpTracker`). */
export const pageEntryStepTracker = new PageEntryStepTracker();

/**
 * Run one step of a page's entry work with the tracker watching it.
 *
 * The `finally` is load-bearing rather than tidy: without it, the first page whose entry
 * work throws leaves its step open for the rest of the session, and because `openStep()`
 * reports the oldest, every later report would name that dead step instead of the live
 * one. Takes the tracker as an argument so tests can drive a clock by hand, and the page
 * so a report can tell whose step it is holding. `entry` is the page's entry number read
 * at the start of its visit (see `currentEntry`); left out, the current one is used.
 */
export async function trackPageEntryStepAsync<T>(
  tracker: PageEntryStepTracker,
  page: string,
  name: string,
  run: () => Promise<T>,
  entry?: number,
): Promise<T> {
  const handle = tracker.begin(page, name, entry);
  try {
    return await run();
  } finally {
    tracker.end(handle);
  }
}

/**
 * Runs one part of a larger entry step and, when the caller is a page entry, times it as
 * a step of its own (#4988's second reading).
 *
 * Code that runs both during a page's entry and at other times (Home's day load also runs
 * on a date change or a synced edit) takes one of these. The entry passes
 * `entryStepRunner(...)`; every other caller passes `untrackedEntryStep`, so a reload
 * that is not part of an entry never shows up in an entry's report.
 */
export type EntryStepRunner = <T>(name: string, run: () => Promise<T>) => Promise<T>;

/** Runs the part and records nothing: for callers that are not a page entry. */
export const untrackedEntryStep: EntryStepRunner = (_name, run) => run();

/** Times each part as a step of `page`'s entry `entry` in `tracker`. */
export function entryStepRunner(tracker: PageEntryStepTracker, page: string, entry: number): EntryStepRunner {
  return (name, run) => trackPageEntryStepAsync(tracker, page, name, run, entry);
}

// ---------------------------------------------------------------------------
// ionViewCanEnter: telling a deliberate refusal from a crash (df-09902f47064a)
// ---------------------------------------------------------------------------
//
// `CsNavComponent.canEnter` runs the third page lifecycle hook, `ionViewCanEnter`,
// and the whole body of its catch block used to be `console.log(e); return false;`.
// So ANY exception thrown while deciding whether a page may be shown was swallowed:
// the navigation was cancelled, the user saw nothing happen, nothing was reported,
// and a `console.log` does not even trip the E2E unexpected-browser-error gate that
// would otherwise have caught it in testing. That is a large part of why #4681 — a
// shared recipe link that opened nothing — went unnoticed for as long as it did.
//
// The reason that catch could not simply report everything it caught is that two of
// the throws reaching it are not failures at all. `AuthService.pageAuthGuardAsync`
// throws when the signed-in user may not see the page, and
// `AuthService.failPageGuardAsync` throws when the record the page was opened for
// cannot be shown. Both are deliberate refusals, and since #4681 both already report
// themselves where they are raised — one through `trackAbnormalError`, the other
// through `trackSilentProblem('PageLoadFailed')` plus a toast. Reporting them again
// from the catch would file every refusal twice.
//
// So the two helpers throw the type below, and the catch reports everything that is
// NOT one of them. Whatever is left is an unexpected exception inside a page's entry
// decision, which is what `trackAbnormalException` is for and what the two waits
// above already do with theirs.

/**
 * Thrown by a page guard helper that has DELIBERATELY refused to show a page and has
 * ALREADY reported that refusal itself.
 *
 * It carries no extra data on purpose. It is not a description of a problem — it is a
 * statement that the problem has been dealt with somewhere else. Everything a reader
 * needs is in the message the helper wrote and in the report it already filed.
 *
 * Throw it ONLY from a place that has just reported. Using it to quiet a report that
 * is merely noisy would put that failure back into the silence this type removes.
 */
export class PageGuardRefusalError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PageGuardRefusalError';
  }
}

/**
 * Is this exception a deliberate page refusal that has already reported itself?
 *
 * Deliberately an `instanceof` test and nothing more. The two ways of getting this
 * wrong do not cost the same. Answering `false` for a real refusal costs one
 * duplicate report, which is visible and easy to fix. Answering `true` for a real
 * crash puts that crash straight back into the silence described above, where by
 * definition nothing will ever tell us about it. So the test is the strict one.
 *
 * That rules out the two looser matches somebody may be tempted to add. Matching the
 * message text is the exact shape this type replaces. Matching `name` alone buys
 * nothing here — there is one bundle and one copy of this class, so anything that is
 * really a refusal passes `instanceof` — while widening what can be mistaken for one.
 */
export function isPageGuardRefusal(err: unknown): boolean {
  return err instanceof PageGuardRefusalError;
}
